import React, { useState, useEffect } from 'react';
import { useLanguage } from '@/App';
import { API } from '@/App';
import axios from 'axios';
import { Check, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

// KVKK Aydinlatma + Acik Riza metin versiyonu — DB'ye consent audit trail ile birlikte kaydedilir.
// Privacy Policy sayfasindaki metin guncellendiginde bu versiyon da artirilir (v1.0 -> v1.1 vb.).
const CONSENT_TEXT_VERSION = 'v1.0';

const InfluencerForm = () => {
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    // Influencer Type — progressive disclosure dallandiricisi (Mom / Mom-to-be / Lifestyle).
    // Bos string ise asagidaki tum bolumler render edilmez.
    influencer_type: '',
    full_name: '',
    email: '',
    // Personal Info — Country/City/Phone conditional yapisi.
    // Default Turkiye; Diger secilirse country_other dolar, phone format degisir, city serbest text olur.
    country_of_residence: 'Türkiye',
    country_other: '',
    phone_country_code: '+90',
    phone_number: '',
    birth_year: '',
    city: '',
    // Family Info (Persona Agency yapisi) — Mom/Mom-to-be conditional, Lifestyle'da hicbiri kullanilmaz.
    // DB'deki eski 85 kayitta legacy alanlar (gender, has_baby, baby1_year, child1_birth_year vb.)
    // dokunulmadan kalir; bu form artik onlari uretmez.
    children_count: '',
    children_birth_dates: [], // [{ month: '', year: '' }, ...]
    is_currently_expecting: null, // null | 'yes' | 'no' (sadece Mom)
    expected_due_month: '',
    expected_due_year: '',
    // Social Media — multi-select dallandiricisi. Checkbox state otoritedir; "username dolu = aktif" mantigi YOK.
    // Eski 'instagram_followers'/'tiktok_followers' (5-band tek field) yerine ayri *_range alanlari.
    active_platforms: [], // ["instagram","tiktok","other"] subset
    // Instagram (sadece active_platforms.includes('instagram') iken doldurulur)
    instagram_username: '',
    instagram_followers_range: '',
    instagram_story_views_range: '',
    instagram_reel_views_range: '',
    // TikTok (sadece active_platforms.includes('tiktok') iken doldurulur)
    tiktok_username: '',
    tiktok_followers_range: '',
    tiktok_video_views_range: '',
    // Other platforms dinamik listesi (sadece active_platforms.includes('other') iken doldurulur)
    // Her satir: { platform, platform_custom_name, username, followers_range }
    other_platforms: [],
    // Content Categories — multi-select 10 kategori (Persona Agency yapisi).
    // Eski tek-string textarea (free-text) yerine canonical Ingilizce value array'i (admin export tutarliligi icin).
    content_categories: [],
    previous_brands: '',
    // Budget Expectations — currency TRY locked, fiyatlar IG/TT seciliyse doldurulur.
    // Eski 'story_price'/'reel_price'/'tiktok_price' (text alanlar) yerine number input + platform-prefix isimlendirme.
    currency: 'TRY',
    instagram_story_price: '',
    instagram_reel_price: '',
    tiktok_video_price: '',
    // Barter — sadece TR'de zorunlu, 'Diger' iken hidden + auto-temizlenir
    barter_open: '',
    // PR Group — double conditional (country='Turkiye' VE barter='yes'); eski 'pr_group' field'i kaldirildi
    pr_group_interest: '',
    // KVKK Kurulu karari 2018/90 — Aydinlatma ve Acik Riza AYRI checkbox.
    // Eski 'kvkk_consent' field'i kaldirildi; backend gecis donemi icin hala kabul ediyor.
    kvkk_aydinlatma_consent: false,
    acik_riza_consent: false,
  });

  const content = {
    tr: {
      // Influencer Type radio (form'un en ustunde, progressive disclosure dallandiricisi)
      typeQuestionTitle: 'Lütfen seçiniz:',
      typeMomLabel: 'Anneyim',
      typeMomDescription: 'Bebeğim, çocuğum / çocuklarım var',
      typeMomToBeLabel: 'Anne adayıyım',
      typeMomToBeDescription: 'Hamileyim, ilk bebeğimi bekliyorum',
      typeLifestyleLabel: 'Lifestyle / Ev Kadını / İş Kadını',
      typeLifestyleDescription: 'Çocuğum yok, lifestyle / yaşam tarzı, ev, yemek, hobi ve kişisel içerikler üretiyorum',
      typeRequired: 'Lütfen influencer tipinizi seçin',
      // Family Info — Mom/Mom-to-be conditional. Lifestyle bu bolumu hic gormez.
      familyInfoTitle: 'Aile Bilgileri',
      childrenCountQuestion: 'Kaç çocuğunuz var?',
      selectChildrenCount: 'Çocuk sayısını seçin',
      childLabel: 'Çocuk',
      birthMonthLabel: 'Doğum Ayı',
      birthYearOfChildLabel: 'Doğum Yılı',
      expectingQuestion: 'Şu anda yeni bir bebek bekliyor musunuz?',
      expectedDueMonth: 'Beklenen Doğum Ayı',
      expectedDueYear: 'Beklenen Doğum Yılı',
      childrenCountRequired: 'Çocuk sayısını seçin',
      childBirthRequired: 'Doğum ay/yıl seçin',
      expectingRequired: 'Lütfen cevaplayın',
      expectedDueRequired: 'Beklenen doğum ay/yıl seçin',
      // Country/Phone/City conditional (Turkiye/Diger)
      countryOfResidenceLabel: 'Türkiye\'de yaşıyorsunuz, değil mi?',
      countryTurkey: 'Evet',
      countryOther: 'Hayır, yurt dışında yaşıyorum',
      countryOtherInputLabel: 'Hangi ülkede yaşıyorsunuz?',
      countryOtherPlaceholder: 'örn. Almanya, Hollanda, ABD',
      phoneLabel: 'Telefon Numaranız (WhatsApp)',
      phoneTrPlaceholder: '(5__) ___ __ __',
      phoneIntlPlaceholder: 'Tam uluslararası format (örn: +49 176 1234567)',
      phoneTrLengthError: 'Telefon numarası 10 haneli olmalı (örn: 5366141896)',
      phoneTrStartsWith5Error: 'Türkiye telefon numarası 5 ile başlamalı',
      cityLabel: 'Şehir',
      cityIntlPlaceholder: 'Şehir (örn: Berlin)',
      countryRequired: 'Lütfen ülke seçin',
      countryOtherRequired: 'Lütfen yaşadığınız ülkeyi yazın',
      cityRequired: 'Lütfen şehir bilgisini girin',
      phoneRequired: 'Lütfen telefon numaranızı girin',
      // Social Media — multi-select (Instagram/TikTok/Other) + dinamik Other listesi
      socialMediaTitle: 'Sosyal Medya Platformları',
      platformQuestion: 'Hangi Platformlarda Aktifsiniz?',
      platformQuestionHelper: 'En az 1 sosyal medya zorunlu',
      platformInstagram: 'Instagram',
      platformTikTok: 'TikTok',
      platformOther: 'Diğer',
      platformOtherSubtitle: '(Belirli platformları ekleyin)',
      igCardTitle: 'Instagram',
      igUsernameLabel: 'Instagram Kullanıcı Adı',
      igUsernamePlaceholder: 'kullaniciadi (@ olmadan)',
      igFollowersLabel: 'Takipçi Sayısı',
      igStoryViewsLabel: 'Ortalama Story Görüntülenme',
      igReelViewsLabel: 'Ortalama Reel Görüntülenme',
      ttCardTitle: 'TikTok',
      ttUsernameLabel: 'TikTok Kullanıcı Adı',
      ttUsernamePlaceholder: 'kullaniciadi',
      ttFollowersLabel: 'Takipçi Sayısı',
      ttVideoViewsLabel: 'Ortalama Video Görüntülenme',
      otherPlatformsTitle: 'Diğer Platformlar',
      otherPlatformLabel: 'Platform',
      otherPlatformSelectPlaceholder: 'Platform seçin',
      otherPlatformCustomLabel: 'Platform Adı',
      otherPlatformCustomPlaceholder: 'Platform adı (örn: Bluesky)',
      otherPlatformUsernameLabel: 'Kullanıcı Adı / URL',
      otherPlatformUsernamePlaceholder: 'kullaniciadi veya URL',
      otherPlatformFollowersLabel: 'Takipçi Sayısı',
      otherPlatformAddBtn: '+ Platform Ekle',
      otherPlatformRemoveBtn: '× Kaldır',
      otherPlatformOptionOtherLabel: 'Diğer (belirtin)',
      selectFollowerRange: 'Takipçi aralığı seçin',
      selectViewRange: 'Aralık seçin',
      platformsRequired: 'En az bir platform seçin',
      igUsernameRequired: 'Instagram kullanıcı adı zorunlu',
      igFollowersRequired: 'Instagram takipçi aralığı zorunlu',
      igStoryRequired: 'Instagram story görüntülenme aralığı zorunlu',
      igReelRequired: 'Instagram reel görüntülenme aralığı zorunlu',
      ttUsernameRequired: 'TikTok kullanıcı adı zorunlu',
      ttFollowersRequired: 'TikTok takipçi aralığı zorunlu',
      ttVideoViewsRequired: 'TikTok video görüntülenme aralığı zorunlu',
      otherListEmpty: 'En az bir platform ekleyin',
      otherRowMissing: 'Satır {n}: tüm alanlar zorunlu',
      // Content Categories — multi-select 10 kategori (Persona Agency yapisi)
      contentCategoriesTitle: 'İçerik Kategorileri',
      contentCategoriesQuestion: 'Hangi kategorilerde içerik üretiyorsunuz? (en az 1 zorunlu)',
      contentCategoriesRequired: 'En az bir içerik kategorisi seçin',
      // Budget Expectations
      budgetTitle: 'Bütçe Beklentileri',
      budgetSubtitle: 'Tek bir gönderi için beklediğiniz fiyat (KDV hariç, TL cinsinden)',
      currencyLabel: 'Para Birimi: TRY (Türk Lirası)',
      igStoryPriceLabel: 'Instagram Story Fiyatı',
      igReelPriceLabel: 'Instagram Reel Fiyatı',
      ttVideoPriceLabel: 'TikTok Video Fiyatı',
      igStoryPriceRequired: 'Instagram Story fiyatını girin',
      igReelPriceRequired: 'Instagram Reel fiyatını girin',
      ttVideoPriceRequired: 'TikTok Video fiyatını girin',
      // Barter (sadece TR'de)
      barterQuestion: 'Ürün/hizmet karşılığı (Barter) iş birliklerine açık mısınız?',
      barterDescription: 'Barter iş birlikleri de yasal reklam duyurusu ve vergi mevzuatına tabidir.',
      barterSelect: 'Seçiniz',
      barterYes: 'Evet',
      barterNo: 'Hayır',
      barterMaybe: 'Belki (markaya göre değişir)',
      barterRequired: 'Barter sorusunu cevaplayın',
      // PR Group (double conditional)
      prGroupQuestion: 'Persona PR Whatsapp Grubu üyeliği ile ilgilenir misiniz?',
      prGroupDescription: 'PR WhatsApp Grubumuzda genellikle barter iş birliklerini duyuruyoruz.',
      prGroupSelect: 'Seçiniz',
      prGroupYes: 'Evet, ilgileniyorum',
      prGroupNo: 'Hayır, teşekkürler',
      prGroupMoreInfo: 'Daha fazla bilgi istiyorum',
      prGroupRequired: 'PR Grubu sorusunu cevaplayın',
      // KVKK consent (Kurul karari 2018/90 — Aydinlatma + Acik Riza ayri)
      aydinlatmaPrefix: 'KVKK ',
      aydinlatmaLinkText: 'Aydınlatma Metnini',
      aydinlatmaSuffix: ' okudum, anladım.',
      acikRizaText: 'Kişisel verilerimin ve özel nitelikli kişisel verilerimin (hamilelik durumu vb.) influencer veritabanına alınması, markalarla paylaşılması, yeni iş fırsatları için iletişim kurulması ve teknik altyapı gereği yurt dışına aktarılmasına ilişkin Açık Rıza Beyanı\'nı okudum, onaylıyorum. ',
      acikRizaLinkText: '(Detayları okuyun)',
      aydinlatmaRequired: 'KVKK Aydınlatma Metnini onaylamanız gerekmektedir',
      acikRizaRequired: 'Açık Rıza Beyanını onaylamanız gerekmektedir',
      title: 'Influencer',
      titleHighlight: 'Ağımıza',
      titleEnd: 'Katılın',
      introText: 'Sizi daha yakından tanıyabilmemiz için formu doldurabilir, veri güvenliği süreçlerimiz hakkında',
      introTextLink: 'KVKK Aydınlatma Metnini',
      introTextEnd: 'inceleyebilirsiniz.',
      trustSignal: 'Influencer ağımızın bir parçası olun',
      subtitle: 'Formu doldurmadan önce KVKK Aydınlatma ve Açık Rıza Metni\'ni okuyunuz.',
      fullName: 'Adınız ve Soyadınız',
      gender: 'Cinsiyetiniz',
      selectGender: 'Cinsiyet Seçiniz',
      female: 'Kadın',
      male: 'Erkek',
      email: 'E-posta Adresiniz',
      whatsapp: 'WhatsApp İletişim Numaranız',
      whatsappNote: 'Başında sıfır olmadan yazınız',
      whatsappPlaceholder: '(5__) ___ __ __',
      birthYear: 'Doğum yılınız nedir?',
      city: 'Yaşadığınız Şehir',
      selectCity: 'Şehir Seçiniz',
      livingAbroad: 'Yurt dışında yaşıyorum',
      hasBaby: '0-3 yaş arası bebeğiniz var mı?',
      baby1: '1. Bebeğinizin doğum yıl ve ayı',
      baby2: '2. Bebeğinizin doğum yıl ve ayı',
      expectedBaby1: '1. Bebeğinizin beklenen doğum yıl ve ayı',
      hasChild: '3 yaşından büyük çocuğunuz var mı?',
      child1BirthYear: '1. Çocuğunuzun doğum yılı',
      child2BirthYear: '2. Çocuğunuzun doğum yılı',
      yearLabel: 'Yıl',
      monthLabel: 'Ay',
      selectYear: 'Yıl Seçiniz',
      selectMonth: 'Ay Seçiniz',
      select: 'Seçiniz',
      instagramUsername: 'Instagram Kullanıcı Adınız',
      instagramFollowers: 'Instagram Takipçi Sayınız',
      selectFollowers: 'Takipçi Aralığı Seçiniz',
      instagramUnder1k: '1000\'den az',
      instagram1k10k: '1 bin - 10 bin arası',
      instagram10k50k: '10 bin - 50 bin arası',
      instagram50k100k: '50 bin - 100 bin arası',
      instagram100kPlus: '100 bin üstü',
      tiktokUsername: 'TikTok Kullanıcı Adınız (Varsa)',
      tiktokFollowers: 'TikTok Takipçi Sayınız (Varsa)',
      tiktokUnder500: '500\'den az',
      tiktok500_5k: '500 - 5 bin arası',
      tiktok5k25k: '5 bin - 25 bin arası',
      tiktok25k50k: '25 bin - 50 bin arası',
      tiktok50kPlus: '50 bin üstü',
      contentCategories: 'Sayfanızda Paylaştığınız Ana İçerik Kategorileri',
      contentCategoriesPlaceholder: 'Örn: Ebeveynlik, Eğitim, Moda, Tarifler vb.',
      previousBrands: 'Daha Önce Marka Tanıtımı Yaptınız mı? Evet ise hangi markalar?',
      storyViews: 'Instagram Story İzlenme Aralığı',
      storyViewsPlaceholder: 'Örn: 2-5 bin arası',
      reelViews: 'Instagram Reel İzlenme Aralığı',
      reelViewsPlaceholder: 'Örn: 5-20 bin arası',
      barterOpen: 'Ürün/Hizmet Karşılığı Tanıtım (Barter) Projelerine Açık mısınız?',
      prGroup: 'Ürün/Hizmet Karşılığı Tanıtım (Barter) Projelerini paylaştığımız PR WhatsApp Grubumuza Sizi Eklememizi İster misiniz?',
      yes: 'Evet',
      no: 'Hayır',
      pregnant: 'Hamileyim',
      notSure: 'Emin değilim',
      storyPrice: 'Instagram - 1 Story Ücret Beklentiniz',
      reelPrice: 'Instagram - 1 Reel Ücret Beklentiniz',
      tiktokPrice: 'TikTok - 1 Reel Ücret Beklentiniz (Varsa)',
      kvkkConsent1: '',
      kvkkConsentLink: 'KVKK Aydınlatma Metni',
      kvkkConsent2: '\'ni okuduğumu ve anladığımı beyan ederim.',
      submit: 'Başvuruyu Gönder',
      submitting: 'Gönderiliyor...',
      successMessage: 'Başvurunuz başarıyla alındı! En kısa sürede sizinle iletişime geçeceğiz.',
      errorMessage: 'Bir hata oluştu. Lütfen tekrar deneyin.',
      required: '(Zorunlu)',
      requiredField: 'Lütfen bu alanı doldurun.',
      invalidEmail: 'Lütfen geçerli bir e-posta adresi girin.',
      selectOption: 'Lütfen bir seçenek belirleyin.'
    },
    en: {
      // Influencer Type radio (top of form, progressive disclosure gate)
      typeQuestionTitle: 'Please select:',
      typeMomLabel: 'Mom',
      typeMomDescription: 'I have a baby, child or children',
      typeMomToBeLabel: 'Mom-to-be',
      typeMomToBeDescription: "I'm pregnant, expecting my first baby",
      typeLifestyleLabel: 'Lifestyle / Homemaker / Businesswoman',
      typeLifestyleDescription: 'No children, I create lifestyle, home, food, hobby and personal content',
      typeRequired: 'Please select your influencer type',
      // Family Info — Mom/Mom-to-be conditional. Lifestyle skips this section.
      familyInfoTitle: 'Family Information',
      childrenCountQuestion: 'How many children do you have?',
      selectChildrenCount: 'Select count',
      childLabel: 'Child',
      birthMonthLabel: 'Birth Month',
      birthYearOfChildLabel: 'Birth Year',
      expectingQuestion: 'Are you currently expecting a new baby?',
      expectedDueMonth: 'Expected Due Month',
      expectedDueYear: 'Expected Due Year',
      childrenCountRequired: 'Please select number of children',
      childBirthRequired: 'Please select birth month/year',
      expectingRequired: 'Please answer',
      expectedDueRequired: 'Please select expected due month/year',
      // Country/Phone/City conditional (Turkey/Other)
      countryOfResidenceLabel: 'You live in Türkiye, right?',
      countryTurkey: 'Yes',
      countryOther: 'No, I live abroad',
      countryOtherInputLabel: 'Which country do you live in?',
      countryOtherPlaceholder: 'e.g. Germany, Netherlands, USA',
      phoneLabel: 'Your Phone Number (WhatsApp)',
      phoneTrPlaceholder: '(5__) ___ __ __',
      phoneIntlPlaceholder: 'Full international format (e.g. +49 176 1234567)',
      phoneTrLengthError: 'Phone number must be 10 digits (e.g., 5366141896)',
      phoneTrStartsWith5Error: 'Turkish phone number must start with 5',
      cityLabel: 'City',
      cityIntlPlaceholder: 'City (e.g. Berlin)',
      countryRequired: 'Please select your country',
      countryOtherRequired: 'Please enter your country of residence',
      cityRequired: 'Please enter your city',
      phoneRequired: 'Please enter your phone number',
      // Social Media — multi-select (Instagram/TikTok/Other) + dynamic Other list
      socialMediaTitle: 'Social Media Platforms',
      platformQuestion: 'Which Platforms Are You Active On?',
      platformQuestionHelper: 'At least 1 social media required',
      platformInstagram: 'Instagram',
      platformTikTok: 'TikTok',
      platformOther: 'Other',
      platformOtherSubtitle: '(Add specific platforms)',
      igCardTitle: 'Instagram',
      igUsernameLabel: 'Instagram Username',
      igUsernamePlaceholder: 'username (without @)',
      igFollowersLabel: 'Follower Count',
      igStoryViewsLabel: 'Avg. Story Views',
      igReelViewsLabel: 'Avg. Reel Views',
      ttCardTitle: 'TikTok',
      ttUsernameLabel: 'TikTok Username',
      ttUsernamePlaceholder: 'username',
      ttFollowersLabel: 'Follower Count',
      ttVideoViewsLabel: 'Avg. Video Views',
      otherPlatformsTitle: 'Other Platforms',
      otherPlatformLabel: 'Platform',
      otherPlatformSelectPlaceholder: 'Select platform',
      otherPlatformCustomLabel: 'Platform Name',
      otherPlatformCustomPlaceholder: 'Platform name (e.g. Bluesky)',
      otherPlatformUsernameLabel: 'Username/Handle/URL',
      otherPlatformUsernamePlaceholder: 'username or URL',
      otherPlatformFollowersLabel: 'Follower Count',
      otherPlatformAddBtn: '+ Add Another Platform',
      otherPlatformRemoveBtn: '× Remove',
      otherPlatformOptionOtherLabel: 'Other (specify)',
      selectFollowerRange: 'Select range',
      selectViewRange: 'Select range',
      platformsRequired: 'Please select at least one platform',
      igUsernameRequired: 'Instagram username is required',
      igFollowersRequired: 'Instagram follower range is required',
      igStoryRequired: 'Instagram story views range is required',
      igReelRequired: 'Instagram reel views range is required',
      ttUsernameRequired: 'TikTok username is required',
      ttFollowersRequired: 'TikTok follower range is required',
      ttVideoViewsRequired: 'TikTok video views range is required',
      otherListEmpty: 'Please add at least one platform',
      otherRowMissing: 'Row {n}: all fields are required',
      // Content Categories — multi-select 10 categories (Persona Agency style)
      contentCategoriesTitle: 'Content Categories',
      contentCategoriesQuestion: 'In which categories do you produce content? (at least 1 required)',
      contentCategoriesRequired: 'Please select at least one content category',
      // Budget Expectations
      budgetTitle: 'Budget Expectations',
      budgetSubtitle: 'Expected price per single post (excluding VAT, in TRY)',
      currencyLabel: 'Currency: TRY (Turkish Lira)',
      igStoryPriceLabel: 'Instagram Story Price',
      igReelPriceLabel: 'Instagram Reel Price',
      ttVideoPriceLabel: 'TikTok Video Price',
      igStoryPriceRequired: 'Please enter Instagram Story price',
      igReelPriceRequired: 'Please enter Instagram Reel price',
      ttVideoPriceRequired: 'Please enter TikTok Video price',
      // Barter (TR-only)
      barterQuestion: 'Are you open to product/service-based (Barter) collaborations?',
      barterDescription: 'Barter collaborations are also subject to legal advertising disclosure and tax regulations.',
      barterSelect: 'Select',
      barterYes: 'Yes',
      barterNo: 'No',
      barterMaybe: 'Maybe (depends on the brand)',
      barterRequired: 'Please answer the Barter question',
      // PR Group (double conditional)
      prGroupQuestion: 'Are you interested in joining the Persona PR WhatsApp Group?',
      prGroupDescription: 'We mainly announce barter collaborations in our PR WhatsApp Group.',
      prGroupSelect: 'Select',
      prGroupYes: "Yes, I'm interested",
      prGroupNo: 'No, thanks',
      prGroupMoreInfo: "I'd like more information",
      prGroupRequired: 'Please answer the PR Group question',
      // KVKK consent (KVKK Board decision 2018/90 — separate Information Notice + Explicit Consent)
      aydinlatmaPrefix: 'I have read and understood the ',
      aydinlatmaLinkText: 'Privacy Policy',
      aydinlatmaSuffix: ' and KVKK Information Text.',
      acikRizaText: 'I have read and I give my explicit consent to the Explicit Consent Statement regarding the processing of my personal data and special categories of personal data (pregnancy status, etc.), inclusion in the influencer database, sharing with brands, contacting for new opportunities, and transfer abroad for technical infrastructure needs. ',
      acikRizaLinkText: '(Read details)',
      aydinlatmaRequired: 'You must accept the Privacy Policy and KVKK Information Text',
      acikRizaRequired: 'You must give your explicit consent',
      title: 'Join Our',
      titleHighlight: 'Influencer',
      titleEnd: 'Network',
      introText: 'You can fill out the form so we can get to know you better, and review our',
      introTextLink: 'KVKK Information Text',
      introTextEnd: 'to learn about our data security processes.',
      trustSignal: 'Become part of our influencer network',
      subtitle: 'Please read the KVKK Information and Consent Text before filling out the form.',
      fullName: 'Full Name',
      gender: 'Gender',
      selectGender: 'Select Gender',
      female: 'Female',
      male: 'Male',
      email: 'Email Address',
      whatsapp: 'WhatsApp Contact Number',
      whatsappNote: 'Write without leading zero',
      whatsappPlaceholder: '(5__) ___ __ __',
      birthYear: 'What is your birth year?',
      city: 'City of Residence',
      selectCity: 'Select City',
      livingAbroad: 'Living abroad',
      hasBaby: 'Do you have a baby (0-3 years old)?',
      baby1: '1. Baby\'s birth year and month',
      baby2: '2. Baby\'s birth year and month',
      expectedBaby1: '1. Baby\'s expected birth year and month',
      hasChild: 'Do you have a child older than 3 years?',
      child1BirthYear: '1. Child\'s birth year',
      child2BirthYear: '2. Child\'s birth year',
      yearLabel: 'Year',
      monthLabel: 'Month',
      selectYear: 'Select Year',
      selectMonth: 'Select Month',
      select: 'Select',
      instagramUsername: 'Instagram Username',
      instagramFollowers: 'Instagram Followers Count',
      selectFollowers: 'Select Follower Range',
      instagramUnder1k: 'Under 1000',
      instagram1k10k: '1k - 10k range',
      instagram10k50k: '10k - 50k range',
      instagram50k100k: '50k - 100k range',
      instagram100kPlus: '100k+',
      tiktokUsername: 'TikTok Username (If any)',
      tiktokFollowers: 'TikTok Followers Count (If any)',
      tiktokUnder500: 'Under 500',
      tiktok500_5k: '500 - 5k range',
      tiktok5k25k: '5k - 25k range',
      tiktok25k50k: '25k - 50k range',
      tiktok50kPlus: '50k+',
      contentCategories: 'Main Content Categories You Share',
      contentCategoriesPlaceholder: 'E.g: Parenting, Education, Fashion, Recipes, etc.',
      previousBrands: 'Have you promoted brands before? If yes, which brands?',
      storyViews: 'Instagram Story Views Range',
      storyViewsPlaceholder: 'E.g: 2-5k range',
      reelViews: 'Instagram Reel Views Range',
      reelViewsPlaceholder: 'E.g: 5-20k range',
      barterOpen: 'Are you open to Product/Service Exchange (Barter) projects?',
      prGroup: 'Would you like to join our PR WhatsApp Group where we share Product/Service Exchange (Barter) Projects?',
      yes: 'Yes',
      no: 'No',
      pregnant: 'I\'m pregnant',
      notSure: 'Not sure',
      storyPrice: 'Instagram - 1 Story Price Expectation',
      reelPrice: 'Instagram - 1 Reel Price Expectation',
      tiktokPrice: 'TikTok - 1 Reel Price Expectation (If any)',
      kvkkConsent1: 'I have read and understand the ',
      kvkkConsentLink: 'KVKK Privacy Notice',
      kvkkConsent2: '.',
      submit: 'Submit Application',
      submitting: 'Submitting...',
      successMessage: 'Your application has been received! We will contact you soon.',
      errorMessage: 'An error occurred. Please try again.',
      required: '(Required)',
      requiredField: 'Please fill out this field.',
      invalidEmail: 'Please enter a valid email address.',
      selectOption: 'Please select an option.'
    }
  };

  const t = content[language];

  // Custom validation handlers
  const handleInvalid = (e) => {
    e.target.setCustomValidity(t.requiredField);
  };

  const handleInputClear = (e) => {
    e.target.setCustomValidity('');
  };

  const handleSelectInvalid = (e) => {
    e.target.setCustomValidity(t.selectOption);
  };

  const handleEmailInvalid = (e) => {
    if (e.target.validity.valueMissing) {
      e.target.setCustomValidity(t.requiredField);
    } else if (e.target.validity.typeMismatch) {
      e.target.setCustomValidity(t.invalidEmail);
    }
  };

  const handleCheckboxInvalid = (e) => {
    if (!e.target.checked) {
      e.target.setCustomValidity(language === 'tr' ? 'Devam etmek için lütfen bu kutuyu işaretleyin' : 'Please check this box if you want to proceed');
    }
  };

  // Generate year options — min 1960, max currentYear-18 (en az 18 yas zorunlu)
  const generateBirthYears = () => {
    const currentYear = new Date().getFullYear();
    const MIN_BIRTH_YEAR = 1960;
    const MAX_BIRTH_YEAR = currentYear - 18;
    return Array.from(
      { length: MAX_BIRTH_YEAR - MIN_BIRTH_YEAR + 1 },
      (_, i) => MAX_BIRTH_YEAR - i
    );
  };

  // Cocuk dogum yili — currentYear-50 ile currentYear arasi.
  // Anne 50-60 yasinda, cocugu 30-40 yasinda olabilir; eski 2008-baslangic varsayimi yetersizdi.
  // Annenin kendi dogum yili (generateBirthYears) etkilenmedi.
  const generateChildBirthYears = () => {
    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 50;
    return Array.from(
      { length: currentYear - minYear + 1 },
      (_, i) => currentYear - i
    );
  };

  const generateBabyYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 4; i--) {
      years.push(i);
    }
    return years;
  };

  const generateExpectedBabyYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear + 1; i >= currentYear - 1; i--) {
      years.push(i);
    }
    return years;
  };

  const generateChildYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 50; i--) {
      years.push(i);
    }
    return years;
  };

  const months = {
    tr: ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'],
    en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
  };

  // Spec'e gore Country of Residence === 'Turkiye' iken kullanilan tam alfabetik 81 il listesi.
  // Eski `turkishCities` (asagida) eski form versiyonu icin tutuluyor — yeni form bu listeyi kullanmaz.
  const TURKIYE_ILLERI = [
    'Adana','Adıyaman','Afyonkarahisar','Ağrı','Aksaray','Amasya','Ankara','Antalya',
    'Ardahan','Artvin','Aydın','Balıkesir','Bartın','Batman','Bayburt','Bilecik',
    'Bingöl','Bitlis','Bolu','Burdur','Bursa','Çanakkale','Çankırı','Çorum','Denizli',
    'Diyarbakır','Düzce','Edirne','Elazığ','Erzincan','Erzurum','Eskişehir','Gaziantep',
    'Giresun','Gümüşhane','Hakkari','Hatay','Iğdır','Isparta','İstanbul','İzmir',
    'Kahramanmaraş','Karabük','Karaman','Kars','Kastamonu','Kayseri','Kilis','Kırıkkale',
    'Kırklareli','Kırşehir','Kocaeli','Konya','Kütahya','Malatya','Manisa','Mardin',
    'Mersin','Muğla','Muş','Nevşehir','Niğde','Ordu','Osmaniye','Rize','Sakarya','Samsun',
    'Şanlıurfa','Siirt','Sinop','Sivas','Şırnak','Tekirdağ','Tokat','Trabzon','Tunceli',
    'Uşak','Van','Yalova','Yozgat','Zonguldak'
  ];

  // Social Media range listeleri (Persona Agency yapisi)
  const IG_FOLLOWERS_RANGES = ['<1K', '1K-10K', '10K-50K', '50K-100K', '100K+']; // 5-band
  const IG_STORY_VIEWS_RANGES = ['<1K', '1K-4K', '4K-10K', '10K-25K', '25K-50K', '50K+']; // 6-band
  const IG_REEL_VIEWS_RANGES = ['<1K', '1K-10K', '10K-50K', '50K-100K', '100K-500K', '500K+']; // 6-band
  const TT_FOLLOWERS_RANGES = ['<1K', '1K-10K', '10K-50K', '50K-100K', '100K+']; // 5-band
  const TT_VIDEO_VIEWS_RANGES = ['<5K', '5K-25K', '25K-50K', '50K-250K', '250K+']; // 5-band
  const OTHER_FOLLOWERS_RANGES = ['<1K', '1K-10K', '10K-50K', '50K-100K', '100K+']; // 5-band
  // Other platform secenekleri — canonical Ingilizce value, label TR'de "Diger (belirtin)" olarak gosterilir.
  const OTHER_PLATFORM_OPTIONS = ['YouTube', 'X (Twitter)', 'Podcast', 'Other (specify)'];

  // Content Categories — backend value Ingilizce kalir (admin export tutarliligi icin), UI label TR/EN ile gosterilir.
  const CONTENT_CATEGORIES = [
    { value: 'Baby Care & Parenting', tr: 'Bebek Bakımı & Ebeveynlik', en: 'Baby Care & Parenting' },
    { value: 'Family Lifestyle', tr: 'Aile Yaşam Tarzı', en: 'Family Lifestyle' },
    { value: 'Mom Fashion & Beauty', tr: 'Anne Modası & Güzellik', en: 'Mom Fashion & Beauty' },
    { value: 'Healthy Cooking & Recipes', tr: 'Sağlıklı Pişirme & Tarifler', en: 'Healthy Cooking & Recipes' },
    { value: 'Home & Decor', tr: 'Ev & Dekorasyon', en: 'Home & Decor' },
    { value: 'Education & Learning', tr: 'Eğitim & Öğrenme', en: 'Education & Learning' },
    { value: 'Family Travel', tr: 'Aile Seyahati', en: 'Family Travel' },
    { value: 'Fitness & Wellness', tr: 'Fitness & Sağlık', en: 'Fitness & Wellness' },
    { value: 'Product Reviews', tr: 'Ürün İncelemeleri', en: 'Product Reviews' },
    { value: 'DIY & Crafts', tr: 'DIY & El İşi', en: 'DIY & Crafts' },
  ];

  const turkishCities = [
    'İstanbul',
    'İzmir',
    'Ankara',
    'Adana',
    'Adıyaman',
    'Afyonkarahisar',
    'Ağrı',
    'Aksaray',
    'Amasya',
    'Antalya',
    'Ardahan',
    'Artvin',
    'Aydın',
    'Balıkesir',
    'Bartın',
    'Batman',
    'Bayburt',
    'Bilecik',
    'Bingöl',
    'Bitlis',
    'Bolu',
    'Burdur',
    'Bursa',
    'Çanakkale',
    'Çankırı',
    'Çorum',
    'Denizli',
    'Diyarbakır',
    'Düzce',
    'Edirne',
    'Elazığ',
    'Erzincan',
    'Erzurum',
    'Eskişehir',
    'Gaziantep',
    'Giresun',
    'Gümüşhane',
    'Hakkâri',
    'Hatay',
    'Iğdır',
    'Isparta',
    'Kahramanmaraş',
    'Karabük',
    'Karaman',
    'Kars',
    'Kastamonu',
    'Kayseri',
    'Kilis',
    'Kırıkkale',
    'Kırklareli',
    'Kırşehir',
    'Kocaeli',
    'Konya',
    'Kütahya',
    'Malatya',
    'Manisa',
    'Mardin',
    'Mersin',
    'Muğla',
    'Muş',
    'Nevşehir',
    'Niğde',
    'Ordu',
    'Osmaniye',
    'Rize',
    'Sakarya',
    'Samsun',
    'Şanlıurfa',
    'Siirt',
    'Sinop',
    'Sivas',
    'Şırnak',
    'Tekirdağ',
    'Tokat',
    'Trabzon',
    'Tunceli',
    'Uşak',
    'Van',
    'Yalova',
    'Yozgat',
    'Zonguldak'
  ];

  // Phone number formatting function
  const formatPhoneNumber = (value) => {
    // Remove all non-digits
    const cleaned = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    const truncated = cleaned.substring(0, 10);
    
    // Apply formatting: (5XX) XXX XX XX
    if (truncated.length === 0) return '';
    if (truncated.length <= 3) return `(${truncated}`;
    if (truncated.length <= 6) return `(${truncated.slice(0, 3)}) ${truncated.slice(3)}`;
    if (truncated.length <= 8) return `(${truncated.slice(0, 3)}) ${truncated.slice(3, 6)} ${truncated.slice(6)}`;
    return `(${truncated.slice(0, 3)}) ${truncated.slice(3, 6)} ${truncated.slice(6, 8)} ${truncated.slice(8)}`;
  };

  // Clean phone number for submission
  const cleanPhoneNumber = (value) => {
    return value.replace(/\D/g, '');
  };

  // country_of_residence degisince ulkeye-bagli alanlar (country_other, city, phone_country_code, phone_number)
  // sifirlanir. Phone formati ve city tipi tamamen degistigi icin eski deger karistirici olur.
  // 'Diger' seciliyse barter ve pr_group_interest da temizlenir (yurtdisi influencer'larda barter mantiksiz).
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      country_other: '',
      city: '',
      phone_country_code: prev.country_of_residence === 'Türkiye' ? '+90' : '',
      phone_number: '',
      ...(prev.country_of_residence === 'Diğer' ? { barter_open: '', pr_group_interest: '' } : {}),
    }));
  }, [formData.country_of_residence]);

  // PR Group sadece barter='yes' veya 'maybe' iken sorulur; aksi halde pr_group_interest temizlenir.
  // ("Belki" diyenler de potansiyel adaydir, onlara da soruyoruz.)
  useEffect(() => {
    if (formData.barter_open !== 'yes' && formData.barter_open !== 'maybe') {
      setFormData(prev => ({ ...prev, pr_group_interest: '' }));
    }
  }, [formData.barter_open]);

  // children_count degisince children_birth_dates array'i auto-resize edilir.
  // Mevcut girilmis ay/yil verisini korur, yeni indeksler bos eklenir, fazlalik kirpilir.
  useEffect(() => {
    if (formData.influencer_type === 'Mom' && formData.children_count) {
      const count = parseInt(formData.children_count, 10);
      setFormData(prev => {
        const current = prev.children_birth_dates || [];
        const newArray = Array.from({ length: count }, (_, i) =>
          current[i] || { month: '', year: '' }
        );
        return { ...prev, children_birth_dates: newArray };
      });
    }
  }, [formData.children_count, formData.influencer_type]);

  // Per-child birth ay/yil guncellemesi
  const handleChildBirthChange = (index, field, value) => {
    setFormData(prev => {
      const arr = [...(prev.children_birth_dates || [])];
      arr[index] = { ...(arr[index] || { month: '', year: '' }), [field]: value };
      return { ...prev, children_birth_dates: arr };
    });
  };

  // active_platforms degisince ilgili alanlar temizlenir; "other" checked olunca otomatik 1 bos satir eklenir.
  useEffect(() => {
    setFormData(prev => {
      const next = { ...prev };
      if (!prev.active_platforms.includes('instagram')) {
        next.instagram_username = '';
        next.instagram_followers_range = '';
        next.instagram_story_views_range = '';
        next.instagram_reel_views_range = '';
        // Budget — IG unchecked olunca IG fiyat alanlari da temizlenir
        next.instagram_story_price = '';
        next.instagram_reel_price = '';
      }
      if (!prev.active_platforms.includes('tiktok')) {
        next.tiktok_username = '';
        next.tiktok_followers_range = '';
        next.tiktok_video_views_range = '';
        // Budget — TT unchecked olunca TT fiyat alani da temizlenir
        next.tiktok_video_price = '';
      }
      if (
        prev.active_platforms.includes('other') &&
        (!prev.other_platforms || prev.other_platforms.length === 0)
      ) {
        next.other_platforms = [
          { platform: '', platform_custom_name: '', username: '', followers_range: '' },
        ];
      }
      if (!prev.active_platforms.includes('other')) {
        next.other_platforms = [];
      }
      return next;
    });
  }, [formData.active_platforms]);

  // Platform multi-select toggle (checkbox state otoritedir)
  const togglePlatform = (platformValue) => {
    setFormData(prev => {
      const isActive = prev.active_platforms.includes(platformValue);
      const newPlatforms = isActive
        ? prev.active_platforms.filter(p => p !== platformValue)
        : [...prev.active_platforms, platformValue];
      return { ...prev, active_platforms: newPlatforms };
    });
  };

  // Content category multi-select toggle
  const toggleCategory = (categoryValue) => {
    setFormData(prev => {
      const isActive = prev.content_categories.includes(categoryValue);
      const newCategories = isActive
        ? prev.content_categories.filter(c => c !== categoryValue)
        : [...prev.content_categories, categoryValue];
      return { ...prev, content_categories: newCategories };
    });
  };

  // Other platforms dinamik liste yardimcilari
  const updateOtherPlatform = (index, field, value) => {
    setFormData(prev => {
      const updated = [...prev.other_platforms];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, other_platforms: updated };
    });
  };

  const addOtherPlatform = () => {
    setFormData(prev => ({
      ...prev,
      other_platforms: [
        ...prev.other_platforms,
        { platform: '', platform_custom_name: '', username: '', followers_range: '' },
      ],
    }));
  };

  const removeOtherPlatform = (index) => {
    setFormData(prev => ({
      ...prev,
      other_platforms: prev.other_platforms.filter((_, i) => i !== index),
    }));
  };

  // TR telefon mask formati: state'teki raw 10 haneli digit string'i "(5__) ___ __ __" olarak gosterir.
  // Input'un value prop'u bu fonksiyondan gecirilir; state hep raw digits tutar.
  const formatPhone = (digits) => {
    if (!digits) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    if (digits.length <= 8) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      let processedValue = type === 'checkbox' ? checked : value;
      // Turkiye modunda phone_number 10 hane (kullanici '5' dahil 10 hane girer; mask UI'da gosterilir).
      // Akilli yapistirma: yapistirilan numara 10 haneden uzunsa basindaki +90 veya 0 prefix'i temizlenir.
      // ('5' kesme YOK — kullanici 5 ile basliyor; eski 9-hane + auto-prefix mantigi kaldirildi.)
      if (name === 'phone_number' && prev.country_of_residence === 'Türkiye') {
        let digits = value.replace(/\D/g, '');
        if (digits.startsWith('90') && digits.length > 10) digits = digits.slice(2);
        if (digits.startsWith('0') && digits.length > 10) digits = digits.slice(1);
        processedValue = digits.slice(0, 10);
      }

      const newData = {
        ...prev,
        [name]: processedValue,
      };

      return newData;
    });
  };

  // Validate fonksiyonlari `{ field, message }` | null donduruyor — handleSubmit ilk hatali alana
  // scroll + focus yapabilsin diye field anahtarini iletmemiz gerekiyor (UX iyilestirmesi).
  // field anahtari DOM'da name attribute / data-testid ile eslesir; scrollToFirstError helper
  // asagida bunu querySelector ile cozer.

  const validatePersonalInfo = () => {
    if (!formData.country_of_residence) return { field: 'country_of_residence', message: t.countryRequired };
    if (formData.country_of_residence === 'Diğer' && !formData.country_other?.trim()) {
      return { field: 'country_other', message: t.countryOtherRequired };
    }
    if (!formData.city?.trim()) return { field: 'city', message: t.cityRequired };
    if (formData.country_of_residence === 'Türkiye') {
      // 10 hane + ilk hane '5' (TR mobil); kullanici masked input'tan '5' dahil giriyor
      if (!formData.phone_number || formData.phone_number.length !== 10) {
        return { field: 'phone_number', message: t.phoneTrLengthError };
      }
      if (!formData.phone_number.startsWith('5')) {
        return { field: 'phone_number', message: t.phoneTrStartsWith5Error };
      }
    } else {
      if (!formData.phone_number?.trim()) return { field: 'phone_number', message: t.phoneRequired };
    }
    return null;
  };

  const validateConsents = () => {
    if (!formData.kvkk_aydinlatma_consent) return { field: 'kvkk_aydinlatma_consent', message: t.aydinlatmaRequired };
    if (!formData.acik_riza_consent) return { field: 'acik_riza_consent', message: t.acikRizaRequired };
    return null;
  };

  const validateContentCategories = () => {
    if (!formData.content_categories || formData.content_categories.length < 1) {
      return { field: 'content_categories', message: t.contentCategoriesRequired };
    }
    return null;
  };

  const validateBudgetBarterPrGroup = () => {
    const showBudget =
      formData.active_platforms.includes('instagram') ||
      formData.active_platforms.includes('tiktok');
    if (showBudget) {
      if (formData.active_platforms.includes('instagram')) {
        if (!formData.instagram_story_price || parseFloat(formData.instagram_story_price) < 0) {
          return { field: 'instagram_story_price', message: t.igStoryPriceRequired };
        }
        if (!formData.instagram_reel_price || parseFloat(formData.instagram_reel_price) < 0) {
          return { field: 'instagram_reel_price', message: t.igReelPriceRequired };
        }
      }
      if (formData.active_platforms.includes('tiktok')) {
        if (!formData.tiktok_video_price || parseFloat(formData.tiktok_video_price) < 0) {
          return { field: 'tiktok_video_price', message: t.ttVideoPriceRequired };
        }
      }
    }
    if (formData.country_of_residence === 'Türkiye') {
      if (!formData.barter_open) return { field: 'barter_open', message: t.barterRequired };
      if ((formData.barter_open === 'yes' || formData.barter_open === 'maybe') && !formData.pr_group_interest) {
        return { field: 'pr_group_interest', message: t.prGroupRequired };
      }
    }
    return null;
  };

  const validateSocialMedia = () => {
    if (!formData.active_platforms || formData.active_platforms.length < 1) {
      // active_platforms checkbox grubunun ilk tikinin name yok; data-testid ile bulunur
      return { field: 'checkbox-platform-instagram', message: t.platformsRequired };
    }
    if (formData.active_platforms.includes('instagram')) {
      if (!formData.instagram_username?.trim()) return { field: 'instagram_username', message: t.igUsernameRequired };
      if (!formData.instagram_followers_range) return { field: 'instagram_followers_range', message: t.igFollowersRequired };
      if (!formData.instagram_story_views_range) return { field: 'instagram_story_views_range', message: t.igStoryRequired };
      if (!formData.instagram_reel_views_range) return { field: 'instagram_reel_views_range', message: t.igReelRequired };
    }
    if (formData.active_platforms.includes('tiktok')) {
      if (!formData.tiktok_username?.trim()) return { field: 'tiktok_username', message: t.ttUsernameRequired };
      if (!formData.tiktok_followers_range) return { field: 'tiktok_followers_range', message: t.ttFollowersRequired };
      if (!formData.tiktok_video_views_range) return { field: 'tiktok_video_views_range', message: t.ttVideoViewsRequired };
    }
    if (formData.active_platforms.includes('other')) {
      if (!formData.other_platforms || formData.other_platforms.length < 1) {
        return { field: 'add-other-platform-btn', message: t.otherListEmpty };
      }
      for (let i = 0; i < formData.other_platforms.length; i++) {
        const p = formData.other_platforms[i];
        const customMissing = p.platform === 'Other (specify)' && !p.platform_custom_name?.trim();
        if (!p.platform || customMissing || !p.username?.trim() || !p.followers_range) {
          return { field: `other-platform-row-${i}`, message: t.otherRowMissing.replace('{n}', i + 1) };
        }
      }
    }
    return null;
  };

  const validateFamilyInfo = () => {
    if (formData.influencer_type === 'Mom') {
      if (!formData.children_count) return { field: 'children_count', message: t.childrenCountRequired };
      for (let i = 0; i < formData.children_birth_dates.length; i++) {
        const c = formData.children_birth_dates[i];
        if (!c || !c.month || !c.year) {
          return { field: `child-block-${i}`, message: `${t.childLabel} #${i + 1}: ${t.childBirthRequired}` };
        }
      }
      if (formData.is_currently_expecting !== 'yes' && formData.is_currently_expecting !== 'no') {
        return { field: 'is_currently_expecting', message: t.expectingRequired };
      }
      if (formData.is_currently_expecting === 'yes') {
        if (!formData.expected_due_month || !formData.expected_due_year) {
          return { field: 'expected_due_month', message: t.expectedDueRequired };
        }
      }
    }
    if (formData.influencer_type === 'Mom-to-be') {
      if (!formData.expected_due_month || !formData.expected_due_year) {
        return { field: 'expected_due_month', message: t.expectedDueRequired };
      }
    }
    return null;
  };

  // Submit'te validation hatasi olunca ilk hatali alana smooth scroll + focus yapar.
  // KRITIK: Mobile Safari'de klavye animasyonu ~250ms; o sirada scroll calistirsak yanlis pozisyona gider.
  // 300ms setTimeout + requestAnimationFrame ile browser'in bir sonraki render frame'inde,
  // layout stabil olduktan sonra scroll calistirilir (Gemini'in mobile UX uyarisi).
  const scrollToFirstError = (fieldName) => {
    if (!fieldName) return;
    setTimeout(() => {
      requestAnimationFrame(() => {
        // 1. name attribute (input/select/textarea cogunlugu)
        let element = document.querySelector(`[name="${fieldName}"]`);
        // 2. data-testid tam eslesme
        if (!element) element = document.querySelector(`[data-testid="${fieldName}"]`);
        // 3. data-testid kismi eslesme (cocuk/other-platform-row indeksli alanlar icin)
        if (!element) element = document.querySelector(`[data-testid*="${fieldName}"]`);
        // 4. Fallback: sayfa ustune scroll
        if (!element) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        const headerOffset = 100;
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({ top: elementPosition - headerOffset, behavior: 'smooth' });
        // Smooth scroll ~500ms; focus'u ondan sonra cagir, scroll position'a etki etmesin diye preventScroll
        setTimeout(() => {
          try {
            if (typeof element.focus === 'function') {
              element.focus({ preventScroll: true });
            }
          } catch (e) {
            // intentionally ignored — bazi elementler focus desteklemez
          }
        }, 500);
      });
    }, 300);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation pipeline — sirali; ilk hata bulanda dur, ilgili alana scroll + focus.
    // Form akisi sirasi: Personal -> Social -> Family -> Content -> Budget/Barter/PR -> Consents.
    const validators = [
      validatePersonalInfo,
      validateSocialMedia,
      validateFamilyInfo,
      validateContentCategories,
      validateBudgetBarterPrGroup,
      validateConsents,
    ];

    for (const validator of validators) {
      const result = validator();
      if (result) {
        setError(result.message);
        setTimeout(() => setError(''), 3000);
        scrollToFirstError(result.field);
        return;
      }
    }

    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      // Telefon: TR icin state zaten 10 haneli ('5' dahil — kullanici masked input'tan giriyor).
      // Yurtdisi: kullanici serbest text giriyor, mask yok. Her iki durumda da phone_number direkt gider.
      // ('5' prefix ekleme mantigi kaldirildi — eski 9-hane + auto-prefix yapisi degistirildi.)
      const finalPhoneNumber = formData.phone_number;

      // Consent audit trail metadata — KVKK uyumlulugu icin denetim/sikayet halinde
      // hangi metin versiyonuna onay verildigini ispat etmek amaciyla DB'ye kaydedilir.
      // IP adresi backend'de alinir (frontend'den gondermek guvenilir degil).
      const consentMetadata = {
        consent_text_version: CONSENT_TEXT_VERSION,
        consent_user_agent: navigator.userAgent,
        consent_timestamp_client: new Date().toISOString(),
      };

      const submissionData = {
        ...formData,
        phone_number: finalPhoneNumber,
        ...consentMetadata,
      };

      await axios.post(`${API}/influencer-signup`, submissionData);
      setSuccess(true);
      setFormData({
        // Tip de resetlenir; aksi halde basari sonrasi alttaki bolumler hala acik gorunur.
        influencer_type: '',
        full_name: '',
        email: '',
        // Personal Info (Country/City/Phone) reset — Turkiye default
        country_of_residence: 'Türkiye',
        country_other: '',
        phone_country_code: '+90',
        phone_number: '',
        birth_year: '',
        city: '',
        // Family Info reset
        children_count: '',
        children_birth_dates: [],
        is_currently_expecting: null,
        expected_due_month: '',
        expected_due_year: '',
        // Social Media reset
        active_platforms: [],
        instagram_username: '',
        instagram_followers_range: '',
        instagram_story_views_range: '',
        instagram_reel_views_range: '',
        tiktok_username: '',
        tiktok_followers_range: '',
        tiktok_video_views_range: '',
        other_platforms: [],
        // Content Categories reset (artik array)
        content_categories: [],
        previous_brands: '',
        // Budget Expectations reset
        currency: 'TRY',
        instagram_story_price: '',
        instagram_reel_price: '',
        tiktok_video_price: '',
        // Barter + PR Group reset
        barter_open: '',
        pr_group_interest: '',
        // KVKK consent reset (2 ayri checkbox)
        kvkk_aydinlatma_consent: false,
        acik_riza_consent: false
      });
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError(t.errorMessage);
      console.error('Form submission error:', err);
      
      // Hide error message after 3 seconds
      setTimeout(() => {
        setError('');
      }, 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="influencer-form" className="py-20 bg-black bg-opacity-75 border-t border-gray-800" data-testid="influencer-form-section">
      {/* Aria-live region — screen reader'a validation hatasi/basari duyurulur (gorunmez, WCAG 2.1 AA) */}
      <div
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
        className="sr-only"
        id="form-error-announcer"
      >
        {error
          ? `${language === 'tr' ? 'Formda hatalar var, lütfen kontrol edin' : 'Form has errors, please review'}: ${error}`
          : ''}
      </div>

      {/* Submit Success Modal — fixed center overlay, 3sn sonra otomatik kaybolur (setTimeout handleSubmit icinde) */}
      {success && (
        <div
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-green-500/20 backdrop-blur border-2 border-green-400 text-green-300 p-6 rounded-xl shadow-2xl z-50 text-center max-w-sm"
          data-testid="success-message"
          role="status"
          aria-live="polite"
        >
          <p className="font-semibold">
            {language === 'tr'
              ? '✓ Teşekkürler, başvuru formunuz alınmıştır!'
              : '✓ Thank you, your application has been received!'}
          </p>
        </div>
      )}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            {t.title} <span className="text-[#F5C518]">{t.titleHighlight}</span> {t.titleEnd}
          </h2>
          <p className="text-[15px] md:text-base text-gray-300 mb-6 leading-relaxed max-w-3xl mx-auto">
            {t.introText}
            <a
              href={language === 'tr' ? '/gizlilik-politikasi' : '/en/privacy-policy'}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#FFDD59] hover:text-yellow-400 font-semibold underline cursor-pointer mx-1"
            >
              {t.introTextLink}
            </a>
            {t.introTextEnd}
          </p>
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg" data-testid="error-message">
            {error}
          </div>
        )}

        {/* Trust Signal */}
        

        <form onSubmit={handleSubmit} className="bg-gray-900 border border-[#FFDD59] rounded-2xl shadow-xl p-8 space-y-6">
          {/* === Influencer Type (progressive disclosure dallandiricisi) === */}
          {/* Tip secilmeden alttaki diger tum bolumler render edilmez. */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-3">
              {t.typeQuestionTitle} <span className="text-red-500">*</span>
            </label>
            <div className="grid sm:grid-cols-3 gap-3">
              {[
                { value: 'Mom', label: t.typeMomLabel, description: t.typeMomDescription },
                { value: 'Mom-to-be', label: t.typeMomToBeLabel, description: t.typeMomToBeDescription },
                { value: 'Lifestyle', label: t.typeLifestyleLabel, description: t.typeLifestyleDescription },
              ].map((opt) => {
                const checked = formData.influencer_type === opt.value;
                return (
                  <label
                    key={opt.value}
                    className={`flex items-start gap-3 p-4 rounded-lg cursor-pointer transition ${
                      checked
                        ? 'border-2 border-yellow-400 bg-yellow-400/10'
                        : 'border border-gray-700 hover:border-yellow-400/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="influencer_type"
                      value={opt.value}
                      checked={checked}
                      onChange={handleChange}
                      onInvalid={(e) => e.target.setCustomValidity(t.typeRequired)}
                      onInput={handleInputClear}
                      required
                      className="mt-1 w-4 h-4 accent-[#FFDD59] cursor-pointer flex-shrink-0"
                      data-testid={`radio-influencer-type-${opt.value}`}
                    />
                    <div>
                      <div className="text-white font-semibold text-sm">{opt.label}</div>
                      <div className="text-gray-400 text-xs mt-1">{opt.description}</div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Tip secilmedikce form'un geri kalani gizli (progressive disclosure) */}
          {formData.influencer_type && (
            <>
          {/* Full Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              {t.fullName} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="full_name"
              value={formData.full_name}
              onChange={handleChange}
              onInvalid={handleInvalid}
              onInput={handleInputClear}
              required
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
              data-testid="input-full-name"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              {t.email} <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onInvalid={handleEmailInvalid}
              onInput={handleInputClear}
              required
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
              data-testid="input-email"
            />
          </div>

          {/* === Country of Residence — Evet/Hayir radio (data shape ayni: 'Türkiye'/'Diğer') === */}
          {/* UI radio yapisina cevrildi; backend, audit trail, Excel export icin country_of_residence
              hala 'Türkiye' veya 'Diğer' string'lerini tutuyor. Sadece UI sunum farkli. */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              {t.countryOfResidenceLabel} <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label
                className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${
                  formData.country_of_residence === 'Türkiye'
                    ? 'border-[#FFDD59] bg-[#FFDD59]/10'
                    : 'border-gray-700 hover:border-gray-500'
                }`}
              >
                <input
                  type="radio"
                  name="country_of_residence"
                  value="Türkiye"
                  checked={formData.country_of_residence === 'Türkiye'}
                  onChange={() =>
                    setFormData(prev => ({ ...prev, country_of_residence: 'Türkiye', country_other: '' }))
                  }
                  required
                  className="mr-2 accent-[#FFDD59]"
                  data-testid="radio-country-turkiye"
                />
                <span className="text-gray-200 text-sm">{t.countryTurkey}</span>
              </label>
              <label
                className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${
                  formData.country_of_residence === 'Diğer'
                    ? 'border-[#FFDD59] bg-[#FFDD59]/10'
                    : 'border-gray-700 hover:border-gray-500'
                }`}
              >
                <input
                  type="radio"
                  name="country_of_residence"
                  value="Diğer"
                  checked={formData.country_of_residence === 'Diğer'}
                  onChange={() =>
                    setFormData(prev => ({ ...prev, country_of_residence: 'Diğer' }))
                  }
                  required
                  className="mr-2 accent-[#FFDD59]"
                  data-testid="radio-country-diger"
                />
                <span className="text-gray-200 text-sm">{t.countryOther}</span>
              </label>
            </div>
          </div>

          {/* country_other — sadece Diger secilirse */}
          {formData.country_of_residence === 'Diğer' && (
            <div>
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                {t.countryOtherInputLabel} <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="country_other"
                value={formData.country_other}
                onChange={handleChange}
                onInvalid={handleInvalid}
                onInput={handleInputClear}
                placeholder={t.countryOtherPlaceholder}
                required
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                data-testid="input-country-other"
              />
            </div>
          )}

          {/* === City (conditional) === */}
          {/* Turkiye: 81 il dropdown (alfabetik). Diger: serbest text input. */}
          {/* DOM sirasi: ulke radio sonrasi -> sehir -> telefon -> dogum yili (UX akisi). */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              {t.cityLabel} <span className="text-red-500">*</span>
            </label>
            {formData.country_of_residence === 'Türkiye' ? (
              <select
                name="city"
                value={formData.city}
                onChange={handleChange}
                onInvalid={handleSelectInvalid}
                onInput={handleInputClear}
                required
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                data-testid="select-city"
              >
                <option value="">{t.selectCity}</option>
                {TURKIYE_ILLERI.map(il => (
                  <option key={il} value={il}>{il}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                onInvalid={handleInvalid}
                onInput={handleInputClear}
                placeholder={t.cityIntlPlaceholder}
                required
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                data-testid="input-city-intl"
              />
            )}
          </div>

          {/* === Phone (conditional format) === */}
          {/* Turkiye: locked +90 + masked input "(5__) ___ __ __" (kullanici 5 dahil 10 hane girer). */}
          {/* Akilli yapistirma: yapistirilan numara 10 haneden uzunsa +90 veya 0 prefix'leri temizlenir. */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              {t.phoneLabel} <span className="text-red-500">*</span>
            </label>
            {formData.country_of_residence === 'Türkiye' ? (
              <div className="flex">
                <span
                  className="bg-gray-700 px-3 py-3 rounded-l border-r border-gray-600 text-gray-300 select-none flex items-center text-sm font-semibold"
                  aria-hidden="true"
                  data-testid="phone-country-code-locked"
                >
                  🇹🇷 +90
                </span>
                <input
                  type="tel"
                  name="phone_number"
                  value={formatPhone(formData.phone_number)}
                  onChange={handleChange}
                  onInvalid={handleInvalid}
                  onInput={handleInputClear}
                  placeholder={t.phoneTrPlaceholder}
                  inputMode="numeric"
                  maxLength={15}
                  required
                  className="flex-1 px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-r focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                  data-testid="input-phone-number-tr"
                />
              </div>
            ) : (
              <input
                type="text"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
                onInvalid={handleInvalid}
                onInput={handleInputClear}
                placeholder={t.phoneIntlPlaceholder}
                required
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                data-testid="input-phone-number-intl"
              />
            )}
          </div>

          {/* Birth Year (tek satir) */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              {t.birthYear} <span className="text-red-500">*</span>
            </label>
            <select
              name="birth_year"
              value={formData.birth_year}
              onChange={handleChange}
              onInvalid={handleSelectInvalid}
              onInput={handleInputClear}
              required
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
              data-testid="select-birth-year"
            >
              <option value="">{t.selectYear}</option>
              {generateBirthYears().map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {/* === Family Info === */}
          {/* Persona Agency yapisi: Mom -> children_count + her cocuk icin ay/yil + is_currently_expecting + (Yes ise) due ay/yil. */}
          {/* Mom-to-be -> sadece due ay/yil. Lifestyle -> bolum hic render edilmez. */}
          {(formData.influencer_type === 'Mom' || formData.influencer_type === 'Mom-to-be') && (
            <div className="space-y-4 bg-gray-800 p-6 rounded-lg border border-gray-700">
              <h3 className="text-yellow-400 text-xl font-semibold mb-2">{t.familyInfoTitle}</h3>

              {/* --- Mom akisi --- */}
              {formData.influencer_type === 'Mom' && (
                <>
                  <div>
                    <label className="block text-gray-200 text-sm mb-2">
                      {t.childrenCountQuestion} <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="children_count"
                      value={formData.children_count}
                      onChange={handleChange}
                      onInvalid={handleSelectInvalid}
                      onInput={handleInputClear}
                      required
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                      data-testid="select-children-count"
                    >
                      <option value="">{t.selectChildrenCount}</option>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => (
                        <option key={n} value={n}>{n}</option>
                      ))}
                    </select>
                  </div>

                  {/* Her cocuk icin birth ay/yil — children_count'a gore array auto-resize edilir */}
                  {formData.children_birth_dates.map((child, idx) => (
                    <div
                      key={idx}
                      className="bg-gray-800/50 p-3 rounded mb-2"
                      data-testid={`child-block-${idx}`}
                    >
                      <div className="text-gray-200 text-sm font-semibold mb-2">
                        {t.childLabel} #{idx + 1} <span className="text-red-500">*</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">{t.birthMonthLabel}</label>
                          <select
                            value={child.month}
                            onChange={(e) => handleChildBirthChange(idx, 'month', e.target.value)}
                            onInvalid={handleSelectInvalid}
                            onInput={handleInputClear}
                            required
                            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                            data-testid={`select-child-${idx}-month`}
                          >
                            <option value="">{t.selectMonth}</option>
                            {months[language].map((m, i) => (
                              <option key={i} value={i + 1}>{m}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-gray-400 mb-1">{t.birthYearOfChildLabel}</label>
                          <select
                            value={child.year}
                            onChange={(e) => handleChildBirthChange(idx, 'year', e.target.value)}
                            onInvalid={handleSelectInvalid}
                            onInput={handleInputClear}
                            required
                            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                            data-testid={`select-child-${idx}-year`}
                          >
                            <option value="">{t.selectYear}</option>
                            {generateChildBirthYears().map(y => (
                              <option key={y} value={y}>{y}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Yeni bebek bekleniyor mu? — Mom-only */}
                  <div>
                    <label className="block text-gray-200 text-sm mb-2">
                      {t.expectingQuestion} <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 cursor-pointer text-gray-200">
                        <input
                          type="radio"
                          name="is_currently_expecting"
                          value="yes"
                          checked={formData.is_currently_expecting === 'yes'}
                          onChange={handleChange}
                          required
                          className="w-4 h-4 accent-yellow-400 cursor-pointer"
                          data-testid="radio-expecting-yes"
                        />
                        <span>{t.yes}</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-gray-200">
                        <input
                          type="radio"
                          name="is_currently_expecting"
                          value="no"
                          checked={formData.is_currently_expecting === 'no'}
                          onChange={handleChange}
                          required
                          className="w-4 h-4 accent-yellow-400 cursor-pointer"
                          data-testid="radio-expecting-no"
                        />
                        <span>{t.no}</span>
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/* --- Beklenen dogum ay/yil: Mom-to-be hep, Mom yalnizca expecting === 'yes' iken --- */}
              {(formData.influencer_type === 'Mom-to-be' ||
                (formData.influencer_type === 'Mom' && formData.is_currently_expecting === 'yes')) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-gray-200 text-sm mb-2">
                      {t.expectedDueMonth} <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="expected_due_month"
                      value={formData.expected_due_month}
                      onChange={handleChange}
                      onInvalid={handleSelectInvalid}
                      onInput={handleInputClear}
                      required
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                      data-testid="select-expected-due-month"
                    >
                      <option value="">{t.selectMonth}</option>
                      {months[language].map((m, i) => (
                        <option key={i} value={i + 1}>{m}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-200 text-sm mb-2">
                      {t.expectedDueYear} <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="expected_due_year"
                      value={formData.expected_due_year}
                      onChange={handleChange}
                      onInvalid={handleSelectInvalid}
                      onInput={handleInputClear}
                      required
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                      data-testid="select-expected-due-year"
                    >
                      <option value="">{t.selectYear}</option>
                      {(() => {
                        const cy = new Date().getFullYear();
                        return [cy, cy + 1].map(y => (
                          <option key={y} value={y}>{y}</option>
                        ));
                      })()}
                    </select>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* === Social Media Platforms === */}
          {/* Multi-select checkbox dallandiricisi: secilen platformlarin card'i conditional render edilir. */}
          {/* Mimari kural: checkbox state otoritedir; "username dolu = aktif" mantigi YOK. */}
          {/* Ust baslik kaldirildi (sadelestirme); soru + alt-yardim metni yeterli. */}
          <div>
            <label className="block text-sm font-semibold text-gray-300">
              {t.platformQuestion}
            </label>
            <p className="text-xs text-gray-400 mb-3">{t.platformQuestionHelper} <span className="text-red-500">*</span></p>

            <div className="grid sm:grid-cols-3 gap-3 mb-6">
              {[
                { value: 'instagram', label: t.platformInstagram, sub: '' },
                { value: 'tiktok', label: t.platformTikTok, sub: '' },
                { value: 'other', label: t.platformOther, sub: t.platformOtherSubtitle },
              ].map(p => {
                const checked = formData.active_platforms.includes(p.value);
                return (
                  <label
                    key={p.value}
                    className={`flex items-center gap-3 p-3 rounded cursor-pointer transition ${
                      checked
                        ? 'border-2 border-yellow-400 bg-yellow-400/10'
                        : 'border border-gray-700 hover:border-yellow-400/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePlatform(p.value)}
                      className="w-4 h-4 accent-yellow-400 cursor-pointer"
                      data-testid={`checkbox-platform-${p.value}`}
                    />
                    <div>
                      <div className="text-white text-sm font-semibold">{p.label}</div>
                      {p.sub && (
                        <div className="text-gray-400 text-xs mt-0.5">{p.sub}</div>
                      )}
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Instagram card (conditional) */}
            {formData.active_platforms.includes('instagram') && (
              <div
                className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 mb-4"
                data-testid="card-instagram"
              >
                <div className="text-yellow-400 font-semibold mb-3">📷 {t.igCardTitle}</div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      {t.igUsernameLabel} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="instagram_username"
                      value={formData.instagram_username}
                      onChange={handleChange}
                      onInvalid={handleInvalid}
                      onInput={handleInputClear}
                      placeholder={t.igUsernamePlaceholder}
                      required
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                      data-testid="input-instagram-username"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        {t.igFollowersLabel} <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="instagram_followers_range"
                        value={formData.instagram_followers_range}
                        onChange={handleChange}
                        onInvalid={handleSelectInvalid}
                        onInput={handleInputClear}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                        data-testid="select-instagram-followers-range"
                      >
                        <option value="">{t.selectFollowerRange}</option>
                        {IG_FOLLOWERS_RANGES.map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        {t.igStoryViewsLabel} <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="instagram_story_views_range"
                        value={formData.instagram_story_views_range}
                        onChange={handleChange}
                        onInvalid={handleSelectInvalid}
                        onInput={handleInputClear}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                        data-testid="select-instagram-story-views"
                      >
                        <option value="">{t.selectViewRange}</option>
                        {IG_STORY_VIEWS_RANGES.map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      {t.igReelViewsLabel} <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="instagram_reel_views_range"
                      value={formData.instagram_reel_views_range}
                      onChange={handleChange}
                      onInvalid={handleSelectInvalid}
                      onInput={handleInputClear}
                      required
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                      data-testid="select-instagram-reel-views"
                    >
                      <option value="">{t.selectViewRange}</option>
                      {IG_REEL_VIEWS_RANGES.map(r => (
                        <option key={r} value={r}>{r}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* TikTok card (conditional) */}
            {formData.active_platforms.includes('tiktok') && (
              <div
                className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 mb-4"
                data-testid="card-tiktok"
              >
                <div className="text-yellow-400 font-semibold mb-3">🎵 {t.ttCardTitle}</div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      {t.ttUsernameLabel} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="tiktok_username"
                      value={formData.tiktok_username}
                      onChange={handleChange}
                      onInvalid={handleInvalid}
                      onInput={handleInputClear}
                      placeholder={t.ttUsernamePlaceholder}
                      required
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                      data-testid="input-tiktok-username"
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        {t.ttFollowersLabel} <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="tiktok_followers_range"
                        value={formData.tiktok_followers_range}
                        onChange={handleChange}
                        onInvalid={handleSelectInvalid}
                        onInput={handleInputClear}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                        data-testid="select-tiktok-followers-range"
                      >
                        <option value="">{t.selectFollowerRange}</option>
                        {TT_FOLLOWERS_RANGES.map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-300 mb-2">
                        {t.ttVideoViewsLabel} <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="tiktok_video_views_range"
                        value={formData.tiktok_video_views_range}
                        onChange={handleChange}
                        onInvalid={handleSelectInvalid}
                        onInput={handleInputClear}
                        required
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                        data-testid="select-tiktok-video-views"
                      >
                        <option value="">{t.selectViewRange}</option>
                        {TT_VIDEO_VIEWS_RANGES.map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Other Platforms — dinamik liste (conditional) */}
            {formData.active_platforms.includes('other') && (
              <div
                className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 mb-4"
                data-testid="card-other-platforms"
              >
                <div className="text-yellow-400 font-semibold mb-3">🌐 {t.otherPlatformsTitle}</div>
                <div className="space-y-3">
                  {formData.other_platforms.map((row, idx) => {
                    const isCustom = row.platform === 'Other (specify)';
                    return (
                      <div
                        key={idx}
                        className="bg-gray-800/50 p-3 rounded mb-2 border border-gray-700"
                        data-testid={`other-platform-row-${idx}`}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm font-semibold text-yellow-400">
                            {t.otherPlatformLabel} #{idx + 1}
                          </span>
                          {formData.other_platforms.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeOtherPlatform(idx)}
                              className="text-red-400 hover:text-red-500 text-sm font-semibold"
                              data-testid={`remove-other-platform-${idx}`}
                            >
                              {t.otherPlatformRemoveBtn}
                            </button>
                          )}
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs text-gray-400 mb-1">
                              {t.otherPlatformLabel} <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={row.platform}
                              onChange={(e) => updateOtherPlatform(idx, 'platform', e.target.value)}
                              onInvalid={handleSelectInvalid}
                              onInput={handleInputClear}
                              required
                              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                              data-testid={`select-other-platform-${idx}`}
                            >
                              <option value="">{t.otherPlatformSelectPlaceholder}</option>
                              {OTHER_PLATFORM_OPTIONS.map(opt => (
                                <option key={opt} value={opt}>
                                  {opt === 'Other (specify)' ? t.otherPlatformOptionOtherLabel : opt}
                                </option>
                              ))}
                            </select>
                          </div>

                          {isCustom && (
                            <div>
                              <label className="block text-xs text-gray-400 mb-1">
                                {t.otherPlatformCustomLabel} <span className="text-red-500">*</span>
                              </label>
                              <input
                                type="text"
                                value={row.platform_custom_name}
                                onChange={(e) =>
                                  updateOtherPlatform(idx, 'platform_custom_name', e.target.value)
                                }
                                onInvalid={handleInvalid}
                                onInput={handleInputClear}
                                placeholder={t.otherPlatformCustomPlaceholder}
                                required
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                                data-testid={`input-other-platform-custom-${idx}`}
                              />
                            </div>
                          )}

                          <div>
                            <label className="block text-xs text-gray-400 mb-1">
                              {t.otherPlatformUsernameLabel} <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={row.username}
                              onChange={(e) => updateOtherPlatform(idx, 'username', e.target.value)}
                              onInvalid={handleInvalid}
                              onInput={handleInputClear}
                              placeholder={t.otherPlatformUsernamePlaceholder}
                              required
                              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                              data-testid={`input-other-platform-username-${idx}`}
                            />
                          </div>

                          <div>
                            <label className="block text-xs text-gray-400 mb-1">
                              {t.otherPlatformFollowersLabel} <span className="text-red-500">*</span>
                            </label>
                            <select
                              value={row.followers_range}
                              onChange={(e) =>
                                updateOtherPlatform(idx, 'followers_range', e.target.value)
                              }
                              onInvalid={handleSelectInvalid}
                              onInput={handleInputClear}
                              required
                              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                              data-testid={`select-other-platform-followers-${idx}`}
                            >
                              <option value="">{t.selectFollowerRange}</option>
                              {OTHER_FOLLOWERS_RANGES.map(r => (
                                <option key={r} value={r}>{r}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    onClick={addOtherPlatform}
                    className="text-yellow-400 hover:text-yellow-500 border border-dashed border-yellow-400/50 px-4 py-2 rounded w-full sm:w-auto"
                    data-testid="add-other-platform-btn"
                  >
                    {t.otherPlatformAddBtn}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* === Content Categories === */}
          {/* 10 sabit kategori, 2-kolon grid (mobile 1-kolon). Backend value Ingilizce kalir; UI label TR/EN. */}
          <div>
            <h3 className="text-yellow-400 text-xl font-semibold mb-4">{t.contentCategoriesTitle}</h3>
            <label className="block text-sm font-semibold text-gray-300 mb-3">
              {t.contentCategoriesQuestion} <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {CONTENT_CATEGORIES.map(cat => {
                const checked = formData.content_categories.includes(cat.value);
                return (
                  <label
                    key={cat.value}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition ${
                      checked
                        ? 'border-2 border-yellow-400 bg-yellow-400/10'
                        : 'bg-gray-800/50 border border-gray-700 hover:border-yellow-400/50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCategory(cat.value)}
                      className="w-4 h-4 accent-yellow-400 cursor-pointer"
                      data-testid={`checkbox-category-${cat.value}`}
                    />
                    <span className="text-gray-200 text-sm">
                      {language === 'tr' ? cat.tr : cat.en}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Previous Brands — opsiyonel (zorunlu degil; * kaldirildi) */}
          <div>
            <label className="block text-sm font-semibold text-gray-300 mb-2">
              {t.previousBrands}
            </label>
            <textarea
              name="previous_brands"
              value={formData.previous_brands}
              onChange={handleChange}
              rows="3"
              className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
              data-testid="input-previous-brands"
            />
          </div>

          {/* === Budget Expectations === */}
          {/* Conditional: sadece IG veya TT seciliyse render. Other'da fiyat sorulmaz. */}
          {(formData.active_platforms.includes('instagram') || formData.active_platforms.includes('tiktok')) && (
            <div>
              <h3 className="text-yellow-400 text-xl font-semibold mb-2">{t.budgetTitle}</h3>
              <p className="text-gray-400 text-sm mb-4">{t.budgetSubtitle}</p>
              <p className="text-gray-400 text-sm mb-3">{t.currencyLabel}</p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {formData.active_platforms.includes('instagram') && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      {t.igStoryPriceLabel} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="instagram_story_price"
                        value={formData.instagram_story_price}
                        onChange={handleChange}
                        onInvalid={handleInvalid}
                        onInput={handleInputClear}
                        required
                        className="w-full pr-12 px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                        data-testid="input-instagram-story-price"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                        TRY
                      </span>
                    </div>
                  </div>
                )}

                {formData.active_platforms.includes('instagram') && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      {t.igReelPriceLabel} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="instagram_reel_price"
                        value={formData.instagram_reel_price}
                        onChange={handleChange}
                        onInvalid={handleInvalid}
                        onInput={handleInputClear}
                        required
                        className="w-full pr-12 px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                        data-testid="input-instagram-reel-price"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                        TRY
                      </span>
                    </div>
                  </div>
                )}

                {formData.active_platforms.includes('tiktok') && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-300 mb-2">
                      {t.ttVideoPriceLabel} <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        name="tiktok_video_price"
                        value={formData.tiktok_video_price}
                        onChange={handleChange}
                        onInvalid={handleInvalid}
                        onInput={handleInputClear}
                        required
                        className="w-full pr-12 px-3 py-2 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                        data-testid="input-tiktok-video-price"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                        TRY
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* === Barter (sadece TR'de) === */}
          {/* Yurtdisi (Diger) icin lojistik dert; useEffect ile country='Diger' iken auto-temizlenir. */}
          {formData.country_of_residence === 'Türkiye' && (
            <div className="mt-4">
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                {t.barterQuestion} <span className="text-red-500">*</span>
              </label>
              <p className="text-gray-400 text-xs mt-1 mb-2 italic">{t.barterDescription}</p>
              <select
                name="barter_open"
                value={formData.barter_open}
                onChange={handleChange}
                onInvalid={handleSelectInvalid}
                onInput={handleInputClear}
                required
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                data-testid="select-barter-open"
              >
                <option value="">{t.barterSelect}</option>
                <option value="yes">{t.barterYes}</option>
                <option value="no">{t.barterNo}</option>
                <option value="maybe">{t.barterMaybe}</option>
              </select>
            </div>
          )}

          {/* === PR Group (double conditional: TR + barter='yes' veya 'maybe') === */}
          {formData.country_of_residence === 'Türkiye' &&
            (formData.barter_open === 'yes' || formData.barter_open === 'maybe') && (
            <div className="mt-6">
              <label className="block text-sm font-semibold text-gray-300 mb-2">
                {t.prGroupQuestion} <span className="text-red-500">*</span>
              </label>
              <p className="text-gray-400 text-xs mt-1 mb-2 italic">{t.prGroupDescription}</p>
              <select
                name="pr_group_interest"
                value={formData.pr_group_interest}
                onChange={handleChange}
                onInvalid={handleSelectInvalid}
                onInput={handleInputClear}
                required
                className="w-full px-4 py-3 bg-gray-800 border border-gray-700 text-white rounded focus:ring-2 focus:ring-[#FFDD59] focus:border-transparent"
                data-testid="select-pr-group-interest"
              >
                <option value="">{t.prGroupSelect}</option>
                <option value="yes">{t.prGroupYes}</option>
                <option value="no">{t.prGroupNo}</option>
                <option value="more_info">{t.prGroupMoreInfo}</option>
              </select>
            </div>
          )}

          {/* === KVKK Aydinlatma + Acik Riza (2 ayri checkbox — Kurul karari 2018/90) === */}
          {/* Hassas veri (cocuk yasi, hamilelik), 3. kisi aktarimi (markalar) ve pazarlama amaci icerdiginden ayri */}
          {/* aydinlatma + acik riza alinmasi zorunlu. */}
          <div className="bg-yellow-400/10 border border-yellow-400/30 rounded-lg p-4 my-6 space-y-4">
            {/* Checkbox 1 — Aydinlatma */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="kvkk_aydinlatma_consent"
                checked={formData.kvkk_aydinlatma_consent}
                onChange={handleChange}
                onInvalid={handleCheckboxInvalid}
                onInput={handleInputClear}
                required
                className="mt-1 w-5 h-5 accent-yellow-400 cursor-pointer flex-shrink-0"
                data-testid="checkbox-kvkk-aydinlatma"
              />
              <span className="text-sm text-gray-200">
                {t.aydinlatmaPrefix}
                <a
                  href={language === 'tr' ? '/gizlilik-politikasi' : '/en/privacy-policy'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow-400 underline hover:text-yellow-500"
                >
                  {t.aydinlatmaLinkText}
                </a>
                {t.aydinlatmaSuffix} <span className="text-red-500">*</span>
              </span>
            </label>

            {/* Checkbox 2 — Acik Riza */}
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                name="acik_riza_consent"
                checked={formData.acik_riza_consent}
                onChange={handleChange}
                onInvalid={handleCheckboxInvalid}
                onInput={handleInputClear}
                required
                className="mt-1 w-5 h-5 accent-yellow-400 cursor-pointer flex-shrink-0"
                data-testid="checkbox-acik-riza"
              />
              <span className="text-sm text-gray-200">
                {t.acikRizaText}
                <a
                  href={language === 'tr' ? '/gizlilik-politikasi#acik-riza' : '/en/privacy-policy#explicit-consent'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-yellow-400 underline hover:text-yellow-500"
                >
                  {t.acikRizaLinkText}
                </a>
                <span className="text-red-500"> *</span>
              </span>
            </label>
          </div>

          {/* Submit Button or Error Message (success modal artik form section'inin disinda fixed-position) */}
          <div className="relative">
            {error ? (
              <div
                className="w-full py-4 bg-red-600 text-white font-bold rounded-lg flex items-center justify-center gap-2"
                data-testid="error-message"
              >
                <span>{error}</span>
              </div>
            ) : (
              <button
                type="submit"
                disabled={loading}
                className="w-full py-4 bg-[#FFDD59] text-black font-bold rounded-lg hover:bg-yellow-500 transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    {t.submitting}
                  </>
                ) : (
                  t.submit
                )}
              </button>
            )}
          </div>
            </>
          )}
        </form>
      </div>
    </section>
  );
};

export default InfluencerForm;
